import { StatefulDatepicker } from 'baseui/datepicker'
import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// components
import FlexContainer from '../../../../components/style/FlexContainer.style'

// style components
import ButtonCardsContainer from '../../style/ButtonCardsContainer.style'

// hardcoded rules
import { hardcodedDailyBookingRules } from '../../../../constants/hardcodedDailyBookingRules.consts'
import { Lot } from '../../../../types/lot.interface'
import { getIsoDurationInDays } from '../../../../utils/getMinOrMaxFromISODuration.utils'
import { DatePickerOverrides, DurationIcon, DurationWarning, Title } from './DailyBooking.style'
import useLanguage from '@hooks/useLanguage.hook'
import { getLocale } from '@utils/getLocale.utils'

interface Props {
    lot: Lot
    startDate: Date | null
    endDate: Date | null
    datepickerVisibilityChange: (visibility: boolean) => void
    onDatesChanges: (startDate: Date | null, endDate: Date | null) => Promise<void>
}

const DailyBooking: FC<Props> = ({ lot, startDate, endDate, datepickerVisibilityChange, onDatesChanges }) => {
    const [randomKey, setRandomKey] = useState(Math.random())
    const [excludedDates, setExcludedDates] = useState<string[]>([])

    const [maxDateRules, setMaxDateRules] = useState<{ maxDate: Date | null; excludeDates: Date[] }>({
        maxDate: null,
        excludeDates: []
    })
    const [minDateRules, setMinDateRules] = useState<{ minDate: Date | null; excludeDates: Date[] }>({
        minDate: null,
        excludeDates: []
    })

    const [minimumBookingDays, setMinimumBookingDays] = useState<number>(1)
    const [maximumBookingDays, setMaximumBookingDays] = useState<number>(365)

    const { t } = useTranslation()
    const [lng] = useLanguage()

    const onDateChange = async (dates: Date | Date[] | null | undefined) => {
        if (dates === null || dates === undefined || !Array.isArray(dates)) {
            onDatesChanges(null, null)
            return
        }
        if (dates.length === 0) {
            onDatesChanges(null, null)
            return
        }
        if (dates.length === 1) {
            if (maximumBookingDays === 1) {
                await onDatesChanges(dates[0], dates[0])
                setRandomKey(Math.random())
                return
            }
            const maxDate = new Date(dates[0])
            maxDate.setDate(maxDate.getDate() + maximumBookingDays - 1)
            const minDate = new Date(dates[0])
            minDate.setDate(minDate.getDate() - maximumBookingDays + 1)
            const latestBookingDate = new Date(lot.end_of_live_date ?? '9999-12-31')
            const earliestBookingDate = new Date(lot.earliest_booking_date ?? new Date())
            const minBookingDate = earliestBookingDate > new Date() ? earliestBookingDate : new Date()

            setMaxDateRules((prev) => {
                return {
                    ...prev,
                    // maxDate should be the minimum of the two
                    maxDate: maxDate < latestBookingDate ? maxDate : latestBookingDate
                }
            })
            const excludedDates: string[] = []
            for (let i = 0; i < minimumBookingDays - 1; i++) {
                const date = new Date(dates[0])
                date.setDate(date.getDate() + i)
                // save the date as dd.mm.yyyy
                excludedDates.push(date.toISOString().split('T')[0])
            }
            for (let i = 0; i < minimumBookingDays - 1; i++) {
                const date = new Date(dates[0])
                date.setDate(date.getDate() - i)

                excludedDates.push(date.toISOString().split('T')[0])
            }

            setMinDateRules((prev) => {
                return {
                    ...prev,
                    minDate: minDate > minBookingDate ? minDate : minBookingDate
                }
            })
            onDatesChanges(dates[0], null)
            setExcludedDates([...excludedDates])
            return
        }
        if (dates && dates.length > 1) {
            onDatesChanges(dates[0], dates[1])
            const latestBookingDate = new Date(lot.end_of_live_date ?? '9999-12-31')
            const earliestBookingDate = new Date(lot.earliest_booking_date ?? new Date())
            const minBookingDate = earliestBookingDate > new Date() ? earliestBookingDate : new Date()

            setMaxDateRules((prev) => {
                return {
                    ...prev,
                    maxDate: latestBookingDate
                }
            })
            setMinDateRules((prev) => {
                return {
                    ...prev,
                    minDate: minBookingDate
                }
            })
            setExcludedDates([])
        }
    }

    useEffect(() => {
        let { maxDate, minDate } = hardcodedDailyBookingRules(lot.id, startDate, endDate)
        const { excludeDates } = hardcodedDailyBookingRules(lot.id, startDate, endDate)
        const earliest_booking_date = lot.earliest_booking_date ? new Date(lot.earliest_booking_date) : new Date()
        const latest_booking_date = new Date(lot.end_of_live_date ?? '9999-12-31')
        const minBookingDays = getIsoDurationInDays(lot.min_booking_duration ?? 'P1D')
        const maxBookingDays = getIsoDurationInDays(lot.max_booking_duration ?? 'P365D')

        // override min and max date with earliest and end of live date
        if (!maxDate) {
            maxDate = latest_booking_date
        }

        if (earliest_booking_date > minDate) {
            minDate = earliest_booking_date
        }

        setMaxDateRules({ maxDate, excludeDates })
        setMinDateRules({ minDate, excludeDates })

        setMinimumBookingDays(minBookingDays)
        setMaximumBookingDays(maxBookingDays)
    }, [lot.id, lot.earliest_booking_date, lot.end_of_live_date, startDate, endDate])

    const filterDate = (day: Date): boolean => {
        return !excludedDates.includes(day.toISOString().split('T')[0])
    }

    return (
        <>
            <Title>{t('lot.dailyBookingTitle')}</Title>
            <ButtonCardsContainer className="pd-range-datepicker" data-testid="daily-booking-datepicker">
                <FlexContainer>
                    <StatefulDatepicker
                        // hack - to force refresh if only 1 day is allowed
                        key={randomKey}
                        clearable={true}
                        aria-label="Select a date"
                        placeholder={t('common.date')}
                        initialState={{ value: [startDate, endDate] }}
                        highlightedDate={minDateRules.minDate}
                        range
                        formatString="dd.MM.yyyy"
                        onOpen={() => datepickerVisibilityChange(true)}
                        onClose={() => datepickerVisibilityChange(false)}
                        separateRangeInputs
                        startDateLabel={t('common.start')}
                        endDateLabel={t('common.end')}
                        onChange={({ date }) => onDateChange(date)}
                        minDate={minDateRules.minDate}
                        maxDate={maxDateRules.maxDate}
                        locale={getLocale(lng)}
                        filterDate={filterDate}
                        overrides={DatePickerOverrides}
                    />
                </FlexContainer>
            </ButtonCardsContainer>
            {maximumBookingDays === 1 && (
                <DurationWarning>
                    <DurationIcon className="wemolo-warning-circle" aria-hidden="true" />
                    <span>{t('lot.lotDailyOneDayWarning')}</span>
                </DurationWarning>
            )}
        </>
    )
}

export default DailyBooking
